import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { CAMPAIGN_WORDS, levelNoun, newLabel, TagLabel } from "@vd/ui";
import { FolderKanban, Plus } from "lucide-react";
import { useState } from "react";
import { type CampaignNode, campaignTreeQuery } from "../../../api/campaigns";
import { requireManager } from "../../../auth/useMe";
import { CampaignDrawer } from "../../../components/campaigns/CampaignDrawer";
import { OwnerAvatar } from "../../../components/campaigns/OwnerAvatar";
import { DataTable } from "../../../components/DataTable";
import { Badge, Button, Card, EmptyState, ErrorState, Loading, PageHeader } from "../../../components/ui";
import { useNavigate } from "../../../lib/navigate";

export const Route = createFileRoute("/admin/campaigns/")({
  staticData: { crumb: { label: CAMPAIGN_WORDS.screen, parents: [] } },
  beforeLoad: requireManager,
  loader: ({ context }) => context.queryClient.prefetchQuery(campaignTreeQuery),
  component: CampaignsPage,
});

function countAll(n: CampaignNode): number {
  return n.children.reduce((s, c) => s + 1 + countAll(c), 0);
}

const col = createColumnHelper<CampaignNode>();
const columns = [
  col.accessor("name", {
    header: levelNoun("brand", { title: true }),
    cell: (c) => (
      <span className="flex items-center gap-2">
        <span className="font-medium text-ink">{c.getValue()}</span>
        <OwnerAvatar node={c.row.original} />
      </span>
    ),
  }),
  col.display({
    id: "subs",
    header: levelNoun("campaign", { title: true, many: true }),
    cell: (c) => <span className="tnum">{countAll(c.row.original)}</span>,
  }),
  col.accessor("list_count", {
    header: "Lists",
    cell: (c) => <span className="tnum">{c.getValue()}</span>,
  }),
  col.display({
    id: "ai",
    header: "Suggestions",
    cell: (c) => (c.row.original.ai.active ? <Badge tone="ok">On</Badge> : <Badge tone="warn">Paused</Badge>),
  }),
  col.display({
    id: "tag",
    header: "Odoo tag",
    cell: (c) => {
      const t = c.row.original.tag;
      return t.state === "ok" && t.name ? (
        <TagLabel path={t.name} />
      ) : t.state === "failed" ? (
        <Badge tone="danger">Not created</Badge>
      ) : (
        <span className="text-ink-3">None</span>
      );
    },
  }),
];

function CampaignsPage() {
  const tree = useQuery(campaignTreeQuery);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title={CAMPAIGN_WORDS.screen}
        actions={
          tree.data?.can_create !== false ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" /> {newLabel(null)}
            </Button>
          ) : null
        }
      />
      {tree.isPending ? (
        <Loading />
      ) : tree.isError ? (
        <ErrorState error={tree.error} onRetry={() => tree.refetch()} />
      ) : (
        <Card>
          <DataTable
            data={tree.data.items}
            columns={columns}
            getRowId={(r) => r.id}
            rowLabel={(r) => r.name}
            onRowClick={(r) => navigate({ to: "/admin/campaigns/$campaignId", params: { campaignId: r.id } })}
            empty={
              <EmptyState
                icon={<FolderKanban />}
                title={`No ${levelNoun("brand", { many: true })} yet`}
                description="Start one, then add the reps who work it."
              />
            }
          />
        </Card>
      )}
      {creating ? (
        <CampaignDrawer
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate({ to: "/admin/campaigns/$campaignId", params: { campaignId: id } })}
        />
      ) : null}
    </>
  );
}
